import type { IconName } from "@prismicio/editor-ui"
import { RichTextNodeType } from "@prismicio/types-internal/lib/customtypes"
import type { ChainedCommands, Editor, Range } from "@tiptap/core"

import {
  Bold,
  BulletList,
  CodeBlock,
  Embed,
  Heading,
  Image,
  Italic,
  OrderedList,
  Paragraph
} from "../extensions"
import { TextDirection } from "../globalExtensions"
import { rightToLeft } from "../globalExtensions/TextDirection"

export type MenuItem = {
  nodeType: RichTextNodeType
  icon: IconName
  isActive: (editor: Editor) => boolean
  runCommands: (editor: Editor, options?: RunCommandsOptions) => boolean
} & (
  | { shortText: string; text: string }
  | { shortText?: undefined; text?: undefined }
)

type RunCommandsOptions = {
  can?: boolean
  deleteRange?: Range
}

export const ALL_NODE_ITEMS: readonly MenuItem[] = [
  {
    nodeType: RichTextNodeType.paragraph,
    icon: "textFields",
    isActive: (editor) =>
      editor.isActive(Paragraph.name) &&
      !editor.isActive(BulletList.name) &&
      !editor.isActive(OrderedList.name),
    runCommands: createRunCommandsFn((commands, editor) =>
      commands.clearNodes().setDirection(getDirection(editor))
    ),
    shortText: "par",
    text: "Paragraph"
  },
  {
    nodeType: RichTextNodeType.heading1,
    icon: "looks1",
    isActive: (editor) => editor.isActive(Heading.name, { level: 1 }),
    runCommands: createRunCommandsFn((commands, editor) =>
      commands.setHeading({ level: 1 }).setDirection(getDirection(editor))
    ),
    shortText: "h1",
    text: "Heading 1"
  },
  {
    nodeType: RichTextNodeType.heading2,
    icon: "looks2",
    isActive: (editor) => editor.isActive(Heading.name, { level: 2 }),
    runCommands: createRunCommandsFn((commands, editor) =>
      commands.setHeading({ level: 2 }).setDirection(getDirection(editor))
    ),
    shortText: "h2",
    text: "Heading 2"
  },
  {
    nodeType: RichTextNodeType.heading3,
    icon: "looks3",
    isActive: (editor) => editor.isActive(Heading.name, { level: 3 }),
    runCommands: createRunCommandsFn((commands, editor) =>
      commands.setHeading({ level: 3 }).setDirection(getDirection(editor))
    ),
    shortText: "h3",
    text: "Heading 3"
  },
  {
    nodeType: RichTextNodeType.heading4,
    icon: "looks4",
    isActive: (editor) => editor.isActive(Heading.name, { level: 4 }),
    runCommands: createRunCommandsFn((commands, editor) =>
      commands.setHeading({ level: 4 }).setDirection(getDirection(editor))
    ),
    shortText: "h4",
    text: "Heading 4"
  },
  {
    nodeType: RichTextNodeType.heading5,
    icon: "looks5",
    isActive: (editor) => editor.isActive(Heading.name, { level: 5 }),
    runCommands: createRunCommandsFn((commands, editor) =>
      commands.setHeading({ level: 5 }).setDirection(getDirection(editor))
    ),
    shortText: "h5",
    text: "Heading 5"
  },
  {
    nodeType: RichTextNodeType.heading6,
    icon: "looks6",
    isActive: (editor) => editor.isActive(Heading.name, { level: 6 }),
    runCommands: createRunCommandsFn((commands, editor) =>
      commands.setHeading({ level: 6 }).setDirection(getDirection(editor))
    ),
    shortText: "h6",
    text: "Heading 6"
  },
  {
    nodeType: RichTextNodeType.list,
    icon: "formatListBulleted",
    isActive: (editor) => editor.isActive(BulletList.name),
    runCommands: createRunCommandsFn((commands, editor) =>
      commands.toggleBulletList().setDirection(getDirection(editor))
    ),
    shortText: "bull",
    text: "Bulleted list"
  },
  {
    nodeType: RichTextNodeType.orderedList,
    icon: "formatListNumbered",
    isActive: (editor) => editor.isActive(OrderedList.name),
    runCommands: createRunCommandsFn((commands, editor) =>
      commands.toggleOrderedList().setDirection(getDirection(editor))
    ),
    shortText: "num",
    text: "Numbered list"
  },
  {
    nodeType: RichTextNodeType.preformatted,
    icon: "dataObject",
    isActive: (editor) => editor.isActive(CodeBlock.name),
    runCommands: createRunCommandsFn((commands, editor) =>
      commands.setCodeBlock().setDirection(getDirection(editor))
    ),
    shortText: "code",
    text: "Code"
  },
  {
    nodeType: RichTextNodeType.embed,
    icon: "cloud",
    isActive: (editor) => editor.isActive(Embed.name),
    runCommands: createRunCommandsFn((commands) => commands.setEmbed()),
    shortText: "emb",
    text: "Embed"
  },
  {
    nodeType: RichTextNodeType.image,
    icon: "image",
    isActive: (editor) => editor.isActive(Image.name),
    runCommands: createRunCommandsFn((commands) => commands.setImage()),
    shortText: "img",
    text: "Image"
  }
]

export const ALL_MARK_ITEMS: readonly MenuItem[] = [
  {
    nodeType: RichTextNodeType.strong,
    icon: "formatBold",
    isActive: (editor) => editor.isActive(Bold.name),
    runCommands: createRunCommandsFn((commands) => commands.toggleBold())
  },
  {
    nodeType: RichTextNodeType.em,
    icon: "formatItalic",
    isActive: (editor) => editor.isActive(Italic.name),
    runCommands: createRunCommandsFn((commands) => commands.toggleItalic())
  }
]

export const ALL_EXTRA_ITEMS: readonly MenuItem[] = [
  {
    nodeType: RichTextNodeType.rtl,
    icon: "formatTextDirectionRToL",
    isActive: (editor) =>
      editor.isActive({
        [TextDirection.attrName]: rightToLeft
      }),
    runCommands: createRunCommandsFn((commands, editor) =>
      commands.toggleDirection(getDirection(editor))
    )
  }
]

export function createRunCommandsFn(
  chainCommands: (commands: ChainedCommands, editor: Editor) => ChainedCommands
): (editor: Editor, options?: RunCommandsOptions) => boolean {
  return (editor, options = {}) => {
    let commands =
      options.can === true ? editor.can().chain() : editor.chain().focus()
    if (options.deleteRange !== undefined)
      commands = commands.deleteRange(options.deleteRange)

    if (options.can) return canRunCommands(editor, commands, chainCommands)

    return chainCommands(commands, editor).run()
  }
}

function getDirection(editor: Editor): TextDirection.Direction {
  const attrs = editor.state.selection.$from.parent.attrs
  return TextDirection.converter.parseNodeAttrsDirection(attrs)
}

export function filterMenuItems(
  menuItems: readonly MenuItem[],
  nodeTypes: readonly string[]
): MenuItem[] {
  return menuItems.filter((menuItem) => nodeTypes.includes(menuItem.nodeType))
}

function canRunCommands(
  editor: Editor,
  commands: ChainedCommands,
  chainCommands: (commands: ChainedCommands, editor: Editor) => ChainedCommands
): boolean {
  try {
    return chainCommands(commands, editor).run()
  } catch {
    return false
  }
}
